import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useActiveEditor } from '../../context/ActiveEditorContext';
import { useSettings } from '../../context/SettingsContext';
import hiveService from '../../services/hive';
import MarkdownRenderer from '../MarkdownRenderer/MarkdownRenderer';
import VotersModal from '../VotersModal/VotersModal';
import { Client } from '@hiveio/dhive';
import HIVE_RPC_NODES from '../../config/hiveNodes';
import { postsApi } from '../../services/api';
import {
  buildOptimisticComment,
  buildOptimisticCommentUpsertPayload
} from '../../utils/optimisticComment';
import { buildOptimisticReplyBody } from '../../utils/optimisticReply';
import useBodyScrollLock from '../../utils/useBodyScrollLock';
import useAuthorAvatar from '../../utils/useAuthorAvatar';
import { buildPostPath, buildFlexDetailPath, getReplyCharacterLimit } from '../../utils/postUrls';
import { formatRelativeTime, formatAbsoluteTime } from '../../utils/time';
import { DEFAULT_FEED_KEY, getFeedKeyByAuthor } from '../../config/feed-details';
import './InlineCommentNode.css';
import VoteSlider from '../VoteSlider/VoteSlider';
import RichEditor from '../RichEditor';
import { htmlToMarkdown } from '../RichEditor/richEditorUtils';

const client = new Client(HIVE_RPC_NODES);

const InlineCommentNode = ({ comment, depth, maxDepth, postAuthor, postPermlink, isDetailView = false, onOptimisticReply, onOptimisticCommentUpdate, noExpand = false }) => {
  const { user, isAuthenticated } = useAuth();
  const { activeEditorId, openEditor, closeEditor } = useActiveEditor();
  const { showVoteSlider: globalShowVoteSlider, voteDefaultWeight, rewardDisplayCurrency, rewardDisplayMode, isUserMuted, removeMutedUser } = useSettings();

  const parseAsset = useCallback((asset) => {
    if (!asset || typeof asset !== 'string') return { amount: null, symbol: null };
    const [amount, symbol] = asset.split(' ');
    return { amount: amount ? parseFloat(amount) : null, symbol: symbol || null };
  }, []);

  const getHivePerHbd = useCallback((price) => {
    const base = parseAsset(price?.base);
    const quote = parseAsset(price?.quote);
    if (!base.amount || !quote.amount || !base.symbol || !quote.symbol) return null;
    if (base.symbol === 'HBD' && quote.symbol === 'HIVE') return quote.amount / base.amount;
    if (base.symbol === 'HIVE' && quote.symbol === 'HBD') return base.amount / quote.amount;
    return null;
  }, [parseAsset]);

  const getDisplayReward = (reward) => {
    if (rewardDisplayMode === 'author_only' && reward > 0) {
      return reward * 0.5;
    }
    return reward;
  };

  const formatRewardValue = (v) => v.toFixed(3);
  const location = useLocation();
  const [replyText, setReplyText] = useState('');
  const [replyHtml, setReplyHtml] = useState('');
  const [isReplying, setIsReplying] = useState(false);
  const [isVoting, setIsVoting] = useState(false);
  const [showVoteSlider, setShowVoteSlider] = useState(false);
  const [voteWeight, setVoteWeight] = useState(voteDefaultWeight || 100);
  const [userVoteWeight, setUserVoteWeight] = useState(null);

  // rewards state
  const [rewardsLoading, setRewardsLoading] = useState(false);
  const [pendingRewardHive, setPendingRewardHive] = useState(0);
  const [pendingRewardHbd, setPendingRewardHbd] = useState(0);
  const [totalPayoutHive, setTotalPayoutHive] = useState(0);
  const [totalPayoutHbd, setTotalPayoutHbd] = useState(0);

  // reset on default changes
  useEffect(() => {
    setUserVoteWeight(null);
    setVoteWeight(voteDefaultWeight || 100);
  }, [voteDefaultWeight]);

  // sync when explicit stored weight appears
  useEffect(() => {
    if (userVoteWeight != null) {
      setVoteWeight(userVoteWeight);
    }
  }, [userVoteWeight]);
  const [showFeedSelector, setShowFeedSelector] = useState(false);
  const [currentFeed, setCurrentFeed] = useState(null);
  const [voteCount, setVoteCount] = useState(comment.votes?.total || comment.votes?.up || 0);
  const [hasVoted, setHasVoted] = useState(false);
  const [showVotersModal, setShowVotersModal] = useState(false);
  const [localReplies, setLocalReplies] = useState([]);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const replies = [...localReplies, ...(Array.isArray(comment.replies) ? comment.replies : [])];
  const hasReplies = replies.length > 0;
  const effectiveMaxDepth = isExpanded ? depth + 2 : maxDepth;
  const isAtMaxDepth = depth >= effectiveMaxDepth;
  const [optimisticStatus, setOptimisticStatus] = useState(comment.optimisticStatus || 'pending');
  const isOptimistic = comment.optimistic;
  const [, setTimerTick] = useState(0); // Force re-renders for relative time updates
  const richEditorRef = useRef(null);
  const editorId = `inline-reply:${comment.author}/${comment.permlink}`;
  const showReply = activeEditorId === editorId;
  const replyCharLimit = getReplyCharacterLimit(comment);
  const { avatarSources, handleAvatarError } = useAuthorAvatar({
    author: comment?.author,
    profileImage: comment?.profileImage
  });
  const markdownDetailOverrides = isDetailView
    ? { forceInlineImages: true }
    : {};
  const [showMutedContent, setShowMutedContent] = useState(false);
  const authorMuted = isUserMuted(comment.author);

  const handleReplyChange = (html) => {
    setReplyHtml(html);
    const markdown = htmlToMarkdown(html);
    setReplyText(markdown);
  };

  useBodyScrollLock(showFeedSelector);

  // Handler to save scroll position when clicking comment link
  const handleCommentLinkClick = () => {
    sessionStorage.setItem('scrollPosition', window.scrollY.toString());
    sessionStorage.setItem('returnPath', location.pathname);
    sessionStorage.setItem('preserveScroll', 'true');
  };

  const updateLocalReply = useCallback((permlink, updates) => {
    setLocalReplies(prev => prev.map(reply => reply.permlink === permlink ? { ...reply, ...updates } : reply));
  }, []);

  // Keep refs to the parent callbacks so the async timeouts always see the latest versions
  const onOptimisticCommentUpdateRef = useRef(onOptimisticCommentUpdate);
  useEffect(() => { onOptimisticCommentUpdateRef.current = onOptimisticCommentUpdate; }, [onOptimisticCommentUpdate]);

  const scheduleReplyConfirmationCheck = useCallback((author, permlink) => {
    const checkDelays = [20000, 60000];

    checkDelays.forEach((delay, index) => {
      setTimeout(async () => {
        const exists = await hiveService.checkPostExists(author, permlink);
        if (exists) {
          if (onOptimisticCommentUpdateRef.current) {
            onOptimisticCommentUpdateRef.current(permlink, { optimistic: false, optimisticStatus: 'confirmed' });
          } else {
            updateLocalReply(permlink, { optimistic: false, optimisticStatus: 'confirmed' });
          }
          return;
        }

        if (index === checkDelays.length - 1) {
          if (onOptimisticCommentUpdateRef.current) {
            onOptimisticCommentUpdateRef.current(permlink, { optimisticStatus: 'failed', optimisticError: 'Not found on-chain yet. Retry?' });
          } else {
            updateLocalReply(permlink, { optimisticStatus: 'failed', optimisticError: 'Not found on-chain yet. Retry?' });
          }
        }
      }, delay);
    });
  }, [updateLocalReply]);

  // If this node represents an optimistic reply loaded from storage we also
  // need to resume the confirmation check after a reload.
  useEffect(() => {
    if (comment && comment.optimistic && comment.optimisticStatus === 'pending') {
      scheduleReplyConfirmationCheck(comment.author, comment.permlink);
    }
  }, [comment, scheduleReplyConfirmationCheck]);

  // Update relative time display every 30 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setTimerTick(prev => prev + 1);
    }, 30000); // Update every 30 seconds
    
    return () => clearInterval(interval);
  }, []);

  const handleRetryOptimistic = async () => {
    if (!comment.parentAuthor || !comment.parentPermlink) return;

    try {
      setOptimisticStatus('pending');
      await hiveService.createComment(
        user?.username || comment.author,
        comment.parentAuthor,
        comment.parentPermlink,
        comment.body,
        comment.rootAuthor || comment.parentAuthor,
        { permlink: comment.permlink }
      );
      scheduleReplyConfirmationCheck(comment.author, comment.permlink);
    } catch (error) {
      setOptimisticStatus('failed');
    }
  };

  const handleReply = async () => {
    if (!isAuthenticated) {
      alert('Please login to comment');
      return;
    }

    if (!replyText.trim()) {
      alert('Please enter a comment');
      return;
    }

    const { normalizedReply, optimisticReplyBody } = buildOptimisticReplyBody({
      replyHtml,
      replyText,
      username: user.username
    });

    setIsReplying(true);
    try {
      const permlink = hiveService.generatePermlinkForReply(comment.author, comment.rootAuthor || comment.parentAuthor);
      const createdAt = new Date().toISOString();
      const jsonMetadata = hiveService.buildCommentMetadata(user.username, normalizedReply, comment.author, comment.rootAuthor || comment.parentAuthor, permlink);

      const optimisticComment = buildOptimisticComment({
        author: user.username,
        permlink,
        body: optimisticReplyBody,
        createdAt,
        jsonMetadata,
        targetPost: { author: comment.author, permlink: comment.permlink },
        rootAuthor: comment.rootAuthor || comment.parentAuthor,
        depth: depth + 1
      });

      // Store optimistic reply — prefer the parent state (FlexDetail) so it
      // survives any re-renders; fall back to local state when used standalone.
      if (onOptimisticReply) {
        onOptimisticReply(comment.author, comment.permlink, optimisticComment);
      } else {
        setLocalReplies(prev => [optimisticComment, ...prev]);
      }

      try {
        await hiveService.createComment(
          user.username,
          comment.author,
          comment.permlink,
          normalizedReply,
          comment.rootAuthor || comment.parentAuthor,
          { permlink }
        );

        await postsApi.upsertOptimisticShortPost(buildOptimisticCommentUpsertPayload({
          author: user.username,
          permlink,
          body: optimisticReplyBody,
          jsonMetadata,
          targetPost: { author: comment.author, permlink: comment.permlink },
          createdAt
        }));

        alert('Reply posted successfully!');
        setReplyText('');
        setReplyHtml('');
        closeEditor(editorId);
        // Only schedule the local check when not delegating to parent
        if (!onOptimisticReply) {
          scheduleReplyConfirmationCheck(user.username, permlink);
        }
      } catch (err) {
        // mark failure in whichever store holds the optimistic comment
        if (onOptimisticCommentUpdateRef.current) {
          onOptimisticCommentUpdateRef.current(permlink, { optimisticStatus: 'failed', optimisticError: err.message || 'Broadcast failed' });
        } else {
          setLocalReplies(prev => prev.map(r => r.permlink === permlink ? { ...r, optimisticStatus: 'failed', optimisticError: err.message || 'Broadcast failed' } : r));
        }
        alert('Failed to post reply: ' + err.message);
      }
    } catch (error) {
      console.error('Reply error:', error);
      alert('Failed to post reply: ' + error.message);
    } finally {
      setIsReplying(false);
    }
  };

  // Fetch live blockchain data
  useEffect(() => {

    const fetchBlockchainData = async () => {
      if (!comment.author || !comment.permlink) return;
      setRewardsLoading(true);
      try {
        const [content, activeVotes, dynamicProps, medianHistoryPrice] = await Promise.all([
          client.database.call('get_content', [comment.author, comment.permlink]),
          client.database.call('get_active_votes', [comment.author, comment.permlink]),
          client.database.call('get_dynamic_global_properties', []),
          client.call('condenser_api', 'get_current_median_history_price', [])
        ]);

        // vote count
        const upvotes = activeVotes.filter(v => v.percent > 0).length;
        setVoteCount(upvotes);
        if (isAuthenticated && user?.username) {
          const userVote = activeVotes.find(v => v.voter === user.username);
          setHasVoted(!!userVote && userVote.percent > 0);
          setUserVoteWeight(userVote && userVote.percent ? Math.max(1, Math.round(userVote.percent / 100)) : null);
        }

        // rewards conversion
        let rewardHive = 0;
        let pendingHBD = 0;
        if (content && content.pending_payout_value) {
          pendingHBD = parseFloat(content.pending_payout_value) || 0;
          const dynamicRate = getHivePerHbd(dynamicProps?.hbd_exchange_rate);
          const medianRate = getHivePerHbd(medianHistoryPrice);
          const hbdExchangeRate = dynamicRate ?? medianRate ?? 1;
          rewardHive = pendingHBD * hbdExchangeRate;
          setPendingRewardHbd(pendingHBD);
          setPendingRewardHive(rewardHive);

          const totalPayoutValue = parseFloat(content.total_payout_value) || 0;
          const curatorPayoutValue = parseFloat(content.curator_payout_value) || 0;
          const totalPayoutHBD = totalPayoutValue + curatorPayoutValue;
          const totalPayoutHIVE = totalPayoutHBD * hbdExchangeRate;
          setTotalPayoutHbd(totalPayoutHBD);
          setTotalPayoutHive(totalPayoutHIVE);
        }

        // optional: cashoutTime not used here
      } catch (error) {
        console.error('Error fetching blockchain data:', error);
      } finally {
        setRewardsLoading(false);
      }
    };

    fetchBlockchainData();
  }, [comment.author, comment.permlink, isAuthenticated, user?.username, rewardDisplayMode, rewardDisplayCurrency, getHivePerHbd]);

  const handleVoteClick = () => {
    if (!isAuthenticated) {
      alert('Please login to vote');
      return;
    }

    if (!globalShowVoteSlider) {
      const wt = voteDefaultWeight || 100;
      handleVote(true, wt);
      return;
    }

    setVoteWeight(userVoteWeight != null ? userVoteWeight : (voteDefaultWeight || 100));
    setShowVoteSlider(!showVoteSlider);
  };

  const handleVote = async (isUpvote = true, explicitWeight = null) => {
    setIsVoting(true);
    try {
      let percent = explicitWeight !== null ? explicitWeight : voteWeight;
      if (typeof percent !== 'number' || isNaN(percent)) {
        percent = voteDefaultWeight || 100;
      }
      const weight = isUpvote ? Math.round(percent * 100) : -Math.round(percent * 100);
      if (isNaN(weight) || Math.abs(weight) > 10000) {
        throw new Error('Vote weight must be between 0 and 10000');
      }
      await hiveService.vote(user.username, comment.author, comment.permlink, weight);
      setShowVoteSlider(false);
      
      // Update vote status immediately
      setHasVoted(true);
      setVoteCount(prev => prev + 1);
      
      alert('Vote submitted successfully! Weight: ' + percent + '%');
    } catch (error) {
      console.error('Vote error:', error);
      alert('Failed to vote: ' + (error.message || String(error)));
    } finally {
      setIsVoting(false);
    }
  };

  const detectCurrentFeed = () => {
    return getFeedKeyByAuthor(comment.parentAuthor) || DEFAULT_FEED_KEY;
  };

  const handleReblogToOtherFeed = () => {
    if (!isAuthenticated) {
      alert('Please login to reblog');
      return;
    }
    const feed = detectCurrentFeed();
    setCurrentFeed(feed);
    setShowFeedSelector(true);
  };

  const rewardBaseValue = rewardDisplayCurrency === 'hbd' ? pendingRewardHbd : pendingRewardHive;
  const rewardDisplayValue = getDisplayReward(rewardBaseValue);
  const rewardUnit = rewardDisplayCurrency === 'hbd' ? 'HBD' : 'HIVE';
  const getRewardTitle = () => {
    if (rewardBaseValue > 0) return '';
    const totalPaid = rewardDisplayCurrency === 'hbd' ? totalPayoutHbd : totalPayoutHive;
    if (totalPaid > 0) {
      return `Paid out: ${formatRewardValue(getDisplayReward(totalPaid))} ${rewardUnit}`;
    }
    return 'No pending rewards';
  };

  const handleFeedSelect = async (feedType) => {
    setShowFeedSelector(false);
    
    try {
      await hiveService.reblogAsComment(
        user.username, 
        comment.author, 
        comment.permlink,
        comment.body,
        feedType
      );
      alert(`Comment reblogged to ${feedType.charAt(0).toUpperCase() + feedType.slice(1)} successfully!`);
    } catch (error) {
      console.error('Reblog error:', error);
      alert('Failed to reblog: ' + error.message);
    }
  };

  const handleVoteCountClick = () => {
    if (voteCount > 0) {
      setShowVotersModal(true);
    }
  };

  useEffect(() => {
    if (comment.optimisticStatus) {
      setOptimisticStatus(comment.optimisticStatus);
    }
  }, [comment.optimisticStatus]);

  if (authorMuted && !showMutedContent) {
    return (
      <div className="inline-comment">
        <div className="inline-comment-header">
          <img
            src={avatarSources[0] || ''}
            alt={comment.author}
            className="comment-avatar"
            data-avatar-fallback-index="0"
            onError={handleAvatarError}
          />
          <Link to={`/user/${comment.author}`} className="comment-author">@{comment.author}</Link>
        </div>
          <div className="inline-comment-body">
            <div className="markdown-renderer markdown-content muted-user">
              <p style={{ display: 'inline-block' }}>Muted content</p>
              <button className="btn showmuted-btn" style={{ display: 'inline-block', float: 'right' }} onClick={() => setShowMutedContent(true)}>Show content</button>
            </div>
          </div>
      </div>
    );
  }

  return (
    <div className="inline-comment">
      <div className="inline-comment-header">
        <img
          src={avatarSources[0] || ''}
          alt={comment.author}
          className="comment-avatar"
          data-avatar-fallback-index="0"
          onError={handleAvatarError}
        />
        <Link to={`/user/${comment.author}`} className="comment-author">
          @{comment.author}
        </Link>
        <span className="comment-time" title={formatAbsoluteTime(comment.created)}>{formatRelativeTime(comment.created)}</span>
        <button
          className="toggle-replies-btn-inline"
          onClick={() => setIsCollapsed(v => !v)}
          aria-expanded={!isCollapsed}
          title={isCollapsed ? 'Expand' : 'Collapse'}
        >{isCollapsed ? '[+]' : '[–]'}</button>
      </div>
      {!isCollapsed && authorMuted && showMutedContent && (
        <div className="muted-content-controls">
          <button className="btn user-unmute-btn" style={{ fontSize: '0.6rem', padding: '6px' }}
            onClick={() => { if (window.confirm(`Unmute @${comment.author}?`)) { removeMutedUser(comment.author); } }}
          >Unmute user</button>
          <button className="btn hide-mutebtn" style={{ marginLeft: '8px', fontSize: '0.6rem', padding: '6px' }}
            onClick={() => setShowMutedContent(false)}
          >Hide content</button>
        </div>
      )}
      {!isCollapsed && (isOptimistic || optimisticStatus === 'confirmed') && (
        <div className={`optimistic-status ${optimisticStatus}`}>
          {optimisticStatus === 'failed' ? (
            <>
              <span>Post not confirmed yet.</span>
              <button className="retry-optimistic-btn" onClick={handleRetryOptimistic}>Retry</button>
            </>
          ) : optimisticStatus === 'confirmed' ? (
            <span>Confirmed</span>
          ) : (
            <span>Posting...</span>
          )}
        </div>
      )}
      {!isCollapsed && (
      <div className="inline-comment-body">
        <MarkdownRenderer content={comment.body} {...markdownDetailOverrides} />
      </div>
      )}
      {!isCollapsed && authorMuted && showMutedContent && (
        <div className="muted-content-controls">
          <button className="btn hide-mutebtn" style={{ fontSize: '0.8rem', padding: '8px 14px' }}
            onClick={() => setShowMutedContent(false)}
          >Hide content</button>
        </div>
      )}
      
      {!isCollapsed && (
      <div className="inline-comment-actions">
        <button 
          className="action-btn"
          onClick={() => (showReply ? closeEditor(editorId) : openEditor(editorId))}
          title="Reply"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M3 10h10a8 8 0 0 1 8 8v2M3 10l6 6m-6-6 6-6" />
          </svg>
        </button>

        <button 
          className={`action-btn like-btn ${hasVoted ? 'voted' : ''}`}
          onClick={handleVoteClick}
          title="Vote"
        >
          <svg viewBox="0 0 24 24" fill={hasVoted ? "var(--accent-primary)" : "none"} stroke={hasVoted ? "var(--accent-primary)" : "currentColor"} strokeWidth="2">
            <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
          </svg>
          <span 
            className={voteCount > 0 ? 'clickable' : ''} 
            onClick={(e) => {
              if (voteCount > 0) {
                e.stopPropagation();
                handleVoteCountClick();
              }
            }}
            style={{ cursor: voteCount > 0 ? 'pointer' : 'default' }}
          >
            {voteCount}
          </span>
        </button>

        <button 
          className="action-btn"
          onClick={handleReblogToOtherFeed}
          title="Repost"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="17 1 21 5 17 9" />
            <path d="M3 11V9a4 4 0 0 1 4-4h14" />
            <polyline points="7 23 3 19 7 15" />
            <path d="M21 13v2a4 4 0 0 1-4 4H3" />
          </svg>
        </button>

        {/* reward display */}
        <button
          className={`action-btn reward-btn ${rewardsLoading ? 'loading' : ''}`}
          onClick={() => setShowVotersModal(true)}
          title={getRewardTitle()}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm3.5 10c.83 0 1.5-.67 1.5-1.5S16.33 9 15.5 9 14 9.67 14 10.5s.67 1.5 1.5 1.5zm-7 0c.83 0 1.5-.67 1.5-1.5S9.33 9 8.5 9 7 9.67 7 10.5 7.67 12 8.5 12zm3.5 6.5c2.33 0 4.31-1.46 5.11-3.5H6.89c.8 2.04 2.78 3.5 5.11 3.5z" />
          </svg>
          {rewardsLoading ? (
            <span className="loading-spinner"></span>
          ) : (
            rewardBaseValue > 0 && <span>{formatRewardValue(rewardDisplayValue)} {rewardUnit}</span>
          )}
        </button>

        {isAtMaxDepth && hasReplies && !noExpand && (
          <button
            className="action-btn hide-on-mobile"
            title="Expand comments"
            onClick={() => setIsExpanded(true)}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
            </svg>
            <span>{replies.length}</span>
          </button>
        )}
        {!isAtMaxDepth && (
          <Link 
            to={buildPostPath(comment.author, comment.permlink)}
            className="action-btn action-link"
            title="View Comment"
            onClick={handleCommentLinkClick}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
              <circle cx="12" cy="12" r="3" />
            </svg>
          </Link>
        )}
      </div>
      )}

      {showFeedSelector && (
        <div className="modal-overlay" onClick={() => setShowFeedSelector(false)}>
          <div className="modal-content feed-selector-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Select a feed to reblog to:</h3>
            <div className="feed-options">
              <button onClick={() => handleFeedSelect('flex')} className={`feed-option-btn ${currentFeed === 'flex' ? 'current-feed' : ''}`}>
                <span className="feed-icon">🔥</span>
                <span className="feed-name">Flex</span>
                {currentFeed === 'flex' && <span className="current-badge">Current</span>}
              </button>
              <button onClick={() => handleFeedSelect('snaps')} className={`feed-option-btn ${currentFeed === 'snaps' ? 'current-feed' : ''}`}>
                <span className="feed-icon">📸</span>
                <span className="feed-name">Snaps</span>
                {currentFeed === 'snaps' && <span className="current-badge">Current</span>}
              </button>
              <button onClick={() => handleFeedSelect('threads')} className={`feed-option-btn ${currentFeed === 'threads' ? 'current-feed' : ''}`}>
                <span className="feed-icon">🧵</span>
                <span className="feed-name">Threads</span>
                {currentFeed === 'threads' && <span className="current-badge">Current</span>}
              </button>
              <button onClick={() => handleFeedSelect('waves')} className={`feed-option-btn ${currentFeed === 'waves' ? 'current-feed' : ''}`}>
                <span className="feed-icon">🌊</span>
                <span className="feed-name">Waves</span>
                {currentFeed === 'waves' && <span className="current-badge">Current</span>}
              </button>
              <button onClick={() => handleFeedSelect('hangs')} className={`feed-option-btn ${currentFeed === 'hangs' ? 'current-feed' : ''}`}>
                <span className="feed-icon">🦥</span>
                <span className="feed-name">Hangs</span>
                {currentFeed === 'hangs' && <span className="current-badge">Current</span>}
              </button>
            </div>
            <button onClick={() => setShowFeedSelector(false)} className="cancel-btn">Cancel</button>
          </div>
        </div>
      )}

      {showVoteSlider && (
        <VoteSlider
          voteWeight={voteWeight}
          setVoteWeight={setVoteWeight}
          onCancel={() => setShowVoteSlider(false)}
          onVote={handleVote}
          isVoting={isVoting}
          showDownvote={false}
          showDebug={false}
          debugData={{ voter: user?.username, author: comment.author, permlink: comment.permlink }}
          userHasVoted={hasVoted}
          userVoteWeight={userVoteWeight}
        />
      )}

      {showVotersModal && (
        <VotersModal 
          author={comment.author} 
          permlink={comment.permlink} 
          onClose={() => setShowVotersModal(false)} 
        />
      )}

      {showReply && (
        <div className="inline-reply-box">
          <RichEditor
            ref={richEditorRef}
            value={replyHtml}
            onChange={handleReplyChange}
            onSubmit={handleReply}
            placeholder="Write a reply..."
            maxLength={replyCharLimit}
            toolbar={true}
          />
          <div className="inline-reply-actions">
            <button
              className="btn-inline btn-inline-primary"
              onClick={handleReply}
              disabled={isReplying || !replyText.trim()}
            >
              {isReplying ? 'Posting...' : 'Post Reply'}
            </button>
            <button
              className="btn-inline btn-inline-secondary"
              onClick={() => {
                closeEditor(editorId);
                setReplyText('');
                setReplyHtml('');
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
      
      {!isCollapsed && hasReplies && (
        <div className="inline-comment-replies">
          {isAtMaxDepth && noExpand ? (
            <Link
              to={buildFlexDetailPath(comment.author, comment.permlink)}
              className="view-deeper-link"
              onClick={handleCommentLinkClick}
            >
              Show deeper replies →
            </Link>
          ) : !isAtMaxDepth ? (
            replies.map((reply) => (
              <InlineCommentNode 
                key={`${reply.author}-${reply.permlink}`}
                comment={reply}
                depth={depth + 1}
                maxDepth={isExpanded ? depth + 2 : maxDepth}
                postAuthor={postAuthor}
                postPermlink={postPermlink}
                onOptimisticReply={onOptimisticReply}
                onOptimisticCommentUpdate={onOptimisticCommentUpdate}
                noExpand={isExpanded || noExpand}
              />
            ))
          ) : null}
        </div>
      )}
    </div>
  );
};

export default React.memo(InlineCommentNode);
